/**
 * =============================================================================
 * Componente: KpiInfoTooltip
 * Versão: 1.0.0 (2026-05-02)
 *
 * Descrição:
 *   Ícone "?" (HelpCircle) clicável/hoverável que abre um tooltip curto
 *   explicando o significado de um KPI no painel do afiliado.
 *
 *   Pode ser plugado ao lado de qualquer label sem reestruturar o componente
 *   pai. Encapsula seu próprio TooltipProvider para funcionar isoladamente
 *   (radix permite providers aninhados sem efeitos colaterais).
 *
 * Uso:
 *   <KpiInfoTooltip label="Saldo disponível" hint="Texto explicativo..." />
 * =============================================================================
 */
import { HelpCircle } from 'lucide-react';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';

interface KpiInfoTooltipProps {
  /** Texto curto explicativo (1-3 linhas). */
  hint: string;
  /** Nome do KPI — usado em aria-label. Default: "este indicador". */
  label?: string;
  /** Lado do tooltip. Default: "top". */
  side?: 'top' | 'bottom' | 'left' | 'right';
  /** Classes extras para o botão (ex.: ajustar opacidade/cor herdada). */
  className?: string;
  /** Tamanho do ícone em px. Default: 12. */
  iconSize?: number;
}

export function KpiInfoTooltip({
  hint,
  label = 'este indicador',
  // Default 'bottom': KPIs costumam ficar no topo de cards (ex.: Hero
  // "Saldo disponível"), e abrir para cima colide com o header global
  // sticky e é cortado pela viewport. Abrindo para baixo o tooltip
  // sempre tem espaço dentro/abaixo do card. Callers cujo gatilho fica
  // no rodapé de um card devem passar side="top" explicitamente.
  side = 'bottom',
  className,
  iconSize = 12,
}: KpiInfoTooltipProps) {
  return (
    <TooltipProvider delayDuration={150}>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            aria-label={`Sobre ${label}`}
            className={cn(
              'inline-flex shrink-0 items-center justify-center rounded-full',
              'opacity-60 hover:opacity-100 transition-opacity',
              'focus:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--aff-primary))]',
              className,
            )}
            // Evita propagar cliques para containers clicáveis (cards, etc.)
            onClick={(e) => e.stopPropagation()}
          >
            <HelpCircle style={{ width: iconSize, height: iconSize }} />
          </button>
        </TooltipTrigger>
        {/*
          Reset tipográfico explícito (normal-case, tracking-normal, font-normal,
          text-left) para neutralizar heranças do label pai — labels de KPI usam
          uppercase + tracking-wider + font-semibold, e sem reset o conteúdo do
          tooltip aparece em CAIXA ALTA e espaçado.

          collisionPadding={12} evita que o tooltip cole nas bordas da viewport
          (problema comum quando o gatilho está perto do topo de cards no Hero
          ou no header do programa).
        */}
        <TooltipContent
          side={side}
          align="center"
          sideOffset={6}
          collisionPadding={16}
          avoidCollisions
          sticky="always"
          className="max-w-[280px] normal-case tracking-normal font-normal text-left text-[12px] leading-relaxed"
        >
          {hint}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
